import type { GitCheckoutInput, GitStatusInput, GitStatusResult } from "@cmmd-forge/contracts";

/**
 * Performing the `Open locally` handoff: switch a local checkout to the branch
 * a finished cloud task produced.
 *
 * ## Why a dirty working tree is a refusal
 *
 * This is the one step that writes to the user's disk, and the instruction came
 * from a web page. Three things could be done with uncommitted changes in the
 * target checkout:
 *
 * - **Refuse** — the user keeps their work and decides what to do.
 * - **Stash** — silently moves their work somewhere they never asked for.
 * - **Force** — destroys it.
 *
 * Refusing is the only one consistent with the rest of this feature: the link
 * refuses rather than sanitizing a branch name, the resolver refuses rather
 * than guessing between worktrees, and the dialog names the directory before
 * touching it. Quietly stashing someone's uncommitted work to service a deep
 * link would undo that entire stance at the last possible step.
 *
 * ## Why "already there" is checked before "dirty"
 *
 * If the checkout is ALREADY on the requested branch there is nothing to
 * switch, so there is no way to lose work — and refusing a no-op because the
 * tree happens to be dirty would be a pointless dead end for the user. Order
 * matters here, and it is pinned by a test.
 */

export type OpenLocalCheckoutResult =
  | { readonly ok: true; readonly changed: boolean }
  | {
      readonly ok: false;
      readonly reason: "not-a-repo" | "dirty" | "checkout-failed";
      readonly message: string;
    };

export interface OpenLocalCheckoutDeps {
  readonly refreshStatus: (input: GitStatusInput) => Promise<GitStatusResult>;
  readonly checkout: (input: GitCheckoutInput) => Promise<unknown>;
}

export async function performOpenLocalCheckout(
  deps: OpenLocalCheckoutDeps,
  input: { readonly cwd: string; readonly branch: string },
): Promise<OpenLocalCheckoutResult> {
  let status: GitStatusResult;
  try {
    status = await deps.refreshStatus({ cwd: input.cwd });
  } catch (error) {
    return {
      ok: false,
      reason: "checkout-failed",
      message: error instanceof Error ? error.message : "Could not read the repository status.",
    };
  }

  if (!status.isRepo) {
    return { ok: false, reason: "not-a-repo", message: `${input.cwd} is not a git repository.` };
  }

  // Before the dirty check: switching to where you already are cannot lose work.
  if (status.branch === input.branch) {
    return { ok: true, changed: false };
  }

  if (status.hasWorkingTreeChanges) {
    return {
      ok: false,
      reason: "dirty",
      message:
        "This checkout has uncommitted changes. Commit or stash them, then open the link again.",
    };
  }

  try {
    await deps.checkout({ cwd: input.cwd, branch: input.branch });
  } catch (error) {
    return {
      ok: false,
      reason: "checkout-failed",
      message: error instanceof Error ? error.message : `Could not switch to ${input.branch}.`,
    };
  }

  return { ok: true, changed: true };
}
